import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useDoctorListByClinicAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useDoctorListByClinicAPI";
import { useOrderTransferInventoryListAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useOrderTransferInventoryListAPI";
import { useCreateClinicToDoctorRequestAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useCreateClinicToDoctorRequestAPI";
import { useGetClinicToDoctorRequestAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useGetClinicToDoctorRequestAPI";
import { useMostSoldByCategoryAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useMostSoldByCategoryAPI";
import { useUpdateClinicToDoctorRequestAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useUpdateClinicToDoctorRequestAPI";
import { InventoryItem, IDefaultTransferListItem } from "@/api_framework/api_modals/Inventory";
import { ICartSearchItemClickResponseData } from "@/api_framework/api_modals/Cart";
import { useTransferOrderSearchAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useTransferOrderSearchAPI";
import { TTransferOrderSearchCategory } from "@/api_framework/api_modals/TransferOrder";
import { useDebounce } from "@uidotdev/usehooks";
import { startCase } from "lodash";
import { toast } from "react-hot-toast";
import { RxCrossCircled } from "react-icons/rx";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { MdOutlineKeyboardDoubleArrowUp } from "react-icons/md";
import { motion } from "motion/react";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { ICartItemAddModalState } from "@/app/components/routes/modals/CartItemAddModal";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { AHS_CLICK_EVENTS, useAhsInventoryClickLog } from "@/app/hooks/useAhsInventoryClickLog";

const TABLE_CELL_CLASS = "text-[0.75rem] font-normal text-black";
const TABLE_CELL_MEDIUM_CLASS = "text-[0.75rem] font-medium text-black";
const TABLE_HEADER_CLASS = "text-[0.875rem] font-semibold text-[#193A6A]";

const CATEGORY_TABS: TTransferOrderSearchCategory[] = ["ALL", "FOOD", "MEDICINE", "CONSUMABLE", "OTHERS"];

const MOST_SOLD_CATEGORIES = ["medicine", "vaccine", "diagnostics"];

const parseExpiryDate = (expiryDate: string): Date | null => {
  if (!expiryDate) return null;
  const parts = expiryDate.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  const fallback = new Date(expiryDate);
  return isNaN(fallback.getTime()) ? null : fallback;
};

/* Highlights lots expiring within the next month so they get transferred/used first */
const isExpiringSoon = (expiryDate: string): boolean => {
  const expiry = parseExpiryDate(expiryDate);
  if (!expiry) return false;
  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  return expiry.getTime() <= oneMonthFromNow.getTime();
};

export interface IGoodInventoryPageProps {
  senderClinic: { id: string; name: string };
  receiverClinic: { id: string; name: string };
  subcategory: Array<string>;
  brand: Array<string>;
  checkedItems: boolean[];
  selectedItems: InventoryItem[];
  transferQty: Array<number>;
  search_query: string;
  transfer_inventory_type: string;
  searchQuery: string;
  variant_category_name?: string;
  variant_category_id?: string;
  clinic_tams: string;
}

interface ICartItem {
  unique_row_id: string;
  vetic_variant_id: string;
  product_name: string;
  lot_number: string;
  manufacturer_name: string;
  expiry_date: string;
  mrp: number;
  qty: number;
  in_hand_qty: number;
  inventory_type: string;
}

const CartQtyEditor = ({
  item,
  index,
  onQtyChange,
}: {
  item: ICartItem;
  index: number;
  onQtyChange: (idx: number, qty: number) => void;
}) => {
  const maxQty = Math.max(1, Math.floor(item.in_hand_qty));
  const [localQty, setLocalQty] = useState<number>(Math.min(item.qty, maxQty));
  const [inputValue, setInputValue] = useState<string>(String(Math.min(item.qty, maxQty)));
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const clamped = Math.min(item.qty, maxQty);
    setLocalQty(clamped);
    setInputValue(String(clamped));
    setError("");
  }, [item.qty]);

  const commit = (val: number, maxExceeded = false) => {
    const clamped = Math.max(1, Math.min(val, maxQty));
    setLocalQty(clamped);
    setInputValue(String(clamped));
    onQtyChange(index, clamped);
    setError(maxExceeded ? `Maximum quantity reached (${maxQty})` : "");
  };

  return (
        <div className="flex items-start gap-1 relative">
          <div>
            <InputWithStartAndEndAddOn
              addonWrapperClassName="mt-0"
              containerClassName="!w-28"
              startAddon="−"
              startAddOnCallback={() => {
                if (localQty > 1) commit(localQty - 1);
                else setError("");
              }}
              endAddon="+"
              endAddOnCallback={() => {
                if (localQty < maxQty) commit(localQty + 1);
                else setError(`Maximum quantity reached (${maxQty})`);
              }}
              min={1}
              max={maxQty}
              value={inputValue}
              inputClassName="!w-12 py-1 text-[0.75rem] leading-normal text-center"
              onChange={(e: any) => {
                const raw = e.target.value;
                if (raw === "") {
                  setInputValue("");
                  setError("");
                  return;
                }
                if (!/^\d+$/.test(raw)) {
                  setError("Only numbers are allowed");
                  return;
                }
                const parsed = parseInt(raw, 10);
                if (parsed > maxQty) {
                  commit(parsed, true);
                } else {
                  setInputValue(raw);
                  setLocalQty(parsed || 1);
                  onQtyChange(index, parsed);
                  setError("");
                }
              }}
              onBlur={() => {
                const parsed = parseInt(inputValue, 10);
                if (inputValue === "" || isNaN(parsed) || parsed < 1) {
                  commit(1);
                }
              }}
              type="text"
              inputMode="numeric"
            />
          </div>
          {error && (
            <p className="absolute top-full left-0 mt-0.5 text-red-500 text-[10px] whitespace-nowrap z-10">
              {error}
            </p>
          )}
        </div>
  );
};

const MostSoldSuggestionList = ({
  label,
  loading,
  items,
  cartItems,
  addingVariantId,
  onAdd,
}: {
  label: string;
  loading: boolean;
  items: IDefaultTransferListItem[];
  cartItems: ICartItem[];
  addingVariantId: string | null;
  onAdd: (item: IDefaultTransferListItem) => void;
}) => {
  const visibleItems = items.filter(
    (item) => !cartItems.some((c) => c.vetic_variant_id === item.vetic_variant_id),
  );

  if (!loading && visibleItems.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium leading-5 text-[#1F1F23]">{label}</span>
      {loading ? (
        <p className="text-xs text-gray-400 py-2">Loading...</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {visibleItems.map((item) => {
            const isAdding = addingVariantId === item.vetic_variant_id;
            return (
              <motion.div
                key={item.vetic_variant_id}
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.55, ease: "easeInOut" }}
                className="py-2 px-3 border border-gray-200 rounded-xl flex flex-wrap justify-between items-center gap-2 bg-white"
              >
                <p className="text-[#1F1F23] font-medium text-sm min-w-0 break-words">{item.product_name}</p>
                <div className="flex gap-3 items-center shrink-0 ml-auto">
                  <span className="bg-[#FFF7F4] py-1.5 px-2.5 font-medium text-xs rounded-full text-[#FF540B] whitespace-nowrap">
                    Suggestion
                  </span>
                  <button
                    type="button"
                    disabled={addingVariantId !== null}
                    onClick={() => onAdd(item)}
                    className="flex gap-1 whitespace-nowrap items-center border-[1.5px] py-1.5 px-2.5 rounded-lg text-sm font-medium border-black text-black bg-[#F6F6F6] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAdding ? "Adding..." : "Add Item"}
                    <MdOutlineKeyboardDoubleArrowUp className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TransferInventory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");
  const { getRouteKey } = useRouter();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const { execute: fetchDoctors, doctorList } = useDoctorListByClinicAPI();
  const { execute: fetchLots, clearSearchResults: clearLotData } = useOrderTransferInventoryListAPI();
  const { execute: createRequest, isSubmitting: isCreating } = useCreateClinicToDoctorRequestAPI();
  const { execute: updateRequest, isSubmitting: isUpdating } = useUpdateClinicToDoctorRequestAPI();
  const { execute: fetchRequest, data: editData, loading: editLoading } = useGetClinicToDoctorRequestAPI();
  const formHook = useForm<{ doctor: string }>({ mode: "onChange" });
  const { logAhsEvent } = useAhsInventoryClickLog();

  const doctorOptions = doctorList.map((d) => ({
    id: d.user_id,
    title: d.email ? `${d.name} (${d.email})` : d.name,
  }));

  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<ICartItem[]>([]);
  const [variantSearchKey, setVariantSearchKey] = useState(0);
  const variantRequestIdRef = useRef(0);
  const autoAddedDiagnosticsRef = useRef<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<TTransferOrderSearchCategory>("ALL");
  const [showResults, setShowResults] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editInitialized, setEditInitialized] = useState(false);
  const [defaultDoctor, setDefaultDoctor] = useState<{ id: string; title: string } | undefined>(undefined);
  const [addingVariantId, setAddingVariantId] = useState<string | null>(null);
  const { sectionsByCategory, loadingByCategory, fetchAll: fetchMostSoldByCategory } = useMostSoldByCategoryAPI();
  const debouncedSearch = useDebounce(searchQuery, 300);
  const { searchData, execute: searchProducts, clearSearchResults } = useTransferOrderSearchAPI();
  const searchRef = useRef<HTMLDivElement>(null);
  const { setVisible: showCartLotModal, setModalData } = useModalActions("CART_ITEM_ADD_MODAL");

  /* Fetch existing request data in edit mode */
  useEffect(() => {
    if (requestId && mappedClinicId) {
      fetchRequest(requestId);
    }
  }, [requestId, mappedClinicId]);

  /* Populate form when edit data arrives */
  useEffect(() => {
    if (editData && !editInitialized) {
      setSelectedDoctorId(editData.doctor_id);
      const doctorOpt = doctorOptions.find((d) => d.id === editData.doctor_id);
      if (doctorOpt) {
        setDefaultDoctor(doctorOpt);
      } else if (editData.doctor_name) {
        setDefaultDoctor({
          id: editData.doctor_id,
          title: `${editData.doctor_name} (${editData.doctor_email ?? ""})`,
        });
      }
      if (editData.items?.length > 0) {
        const mapped = editData.items.map((item) => ({
          unique_row_id: item.unique_row_id,
          vetic_variant_id: item.vetic_variant_id,
          product_name: item.name,
          lot_number: item.lot_details?.lot_number ?? "",
          manufacturer_name: item.lot_details?.manufacturer_name ?? "",
          expiry_date: item.lot_details?.expiry_date ?? "",
          mrp: item.lot_details?.mrp ?? 0,
          qty: item.qty,
          in_hand_qty: 99999,
          inventory_type: item.source_type ?? "clinic",
        }));
        setCartItems(mapped);
      }
      setEditInitialized(true);
    }
  }, [editData, editInitialized, doctorOptions]);

  useEffect(() => {
    if (debouncedSearch.length >= 3 && mappedClinicId) {
      searchProducts(
        {
          clinic_id: mappedClinicId,
          transfer_inventory_type: "good_inventory",
          include_mapped_warehouse: true,
          exclude_low_stock: true,
          category: selectedCategory,
          transfer_direction: "C2D",
        },
        debouncedSearch,
      );
      setShowResults(true);
    } else {
      clearSearchResults();
      setShowResults(false);
    }
  }, [debouncedSearch, mappedClinicId, selectedCategory]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mappedClinicId) {
      fetchDoctors(mappedClinicId);
    }
  }, [mappedClinicId]);

  /* Fetch most-sold suggestion lists for medicine/vaccine/diagnostics in parallel (cached for 5 min) */
  useEffect(() => {
    if (mappedClinicId) {
      fetchMostSoldByCategory(mappedClinicId, MOST_SOLD_CATEGORIES);
    }
  }, [mappedClinicId]);

  const handleVariantSelect = async (item: { name: string; id: string }) => {
    if (!item || !mappedClinicId) return;
    const requestId = ++variantRequestIdRef.current;
    clearLotData();
    const response = await fetchLots(
      { clinic_id: mappedClinicId, transfer_inventory_type: "good_inventory", vetic_variant_id: item.id, include_mapped_warehouse: true, exclude_low_stock: true },
      "100",
      "1",
    );
    // A newer variant selection has started since this fetch began; ignore this stale response.
    if (requestId !== variantRequestIdRef.current) return;
    if (!response?.status || !response.data?.data) {
      toast.error("Failed to fetch product lots");
      return;
    }

    const lotsForVariant = response.data.data.filter(
      (lot) => lot.vetic_variant_id === item.id && lot.quantity > 0,
    );

    if (lotsForVariant.length === 1) {
      handleAddToCart(lotsForVariant[0], 1, item.name);
      setVariantSearchKey((k) => k + 1);
    } else if (lotsForVariant.length > 1) {
      const cartVariants: ICartSearchItemClickResponseData[] = lotsForVariant.map((lot) => ({
        id: lot.unique_row_id,
        name: item.name,
        unique_row_id: lot.unique_row_id,
        qty: 0,
        auto_select: null,
        in_hand_qty: lot.quantity,
        unit_price: lot.mrp,
        gross_price: lot.mrp,
        net_price: lot.mrp,
        lot_details: {
          vetbuddy_lot_number: lot.lot_number,
          expiry_date: lot.expiry_date,
          vetbuddy_manufacturer_name: lot.manufacturer_name,
          vetbuddy_mrp: lot.mrp,
        },
        variant_id: lot.vetic_variant_id,
      }));
      const onItemsAdd = (items: ICartSearchItemClickResponseData[]) => {
        items.forEach((selected) => {
          const originalLot = lotsForVariant.find((l) => l.unique_row_id === selected.unique_row_id);
          if (originalLot) handleAddToCart(originalLot, selected.qty, item.name);
        });
        setVariantSearchKey((k) => k + 1);
      };
      setModalData({ cartVariants, onItemsAdd, source: "clinic_to_doctor_transfer" } as ICartItemAddModalState);
      showCartLotModal();
    } else {
      toast.error("No stock available for this product");
    }
  };

  const handleMostSoldAdd = async (item: IDefaultTransferListItem) => {
    if (addingVariantId) return;
    setAddingVariantId(item.vetic_variant_id);
    try {
      await handleVariantSelect({ name: item.product_name, id: item.vetic_variant_id });
    } finally {
      setAddingVariantId(null);
    }
  };

  const handleAddToCart = (lot: InventoryItem, qty: number, variantName?: string) => {
    setCartItems((prev) => {
      const existing = prev.find((c) => c.unique_row_id === lot.unique_row_id);
      if (existing) {
        return prev.map((c) =>
          c.unique_row_id === lot.unique_row_id
            ? { ...c, qty: Math.min(c.qty + qty, c.in_hand_qty) }
            : c,
        );
      }
      return [
        ...prev,
        {
          unique_row_id: lot.unique_row_id,
          vetic_variant_id: lot.vetic_variant_id,
          product_name: variantName ?? lot.product_name,
          lot_number: lot.lot_number,
          manufacturer_name: lot.manufacturer_name,
          expiry_date: lot.expiry_date,
          mrp: lot.mrp,
          qty: Math.min(qty, lot.quantity),
          in_hand_qty: lot.quantity,
          inventory_type: lot.source_type ?? "clinic",
        },
      ];
    });
  };

  /* Auto-add each "Most Sold Diagnostics" item's highest-quantity lot to the cart (create flow only, 8am-2pm only) */
  useEffect(() => {
    const diagnosticsItems = sectionsByCategory["diagnostics"];
    const currentHour = new Date().getHours();
    const isWithinAutoAddWindow = currentHour >= 8 && currentHour < 14;
    if (!diagnosticsItems?.length || !mappedClinicId || requestId || !isWithinAutoAddWindow) return;

    diagnosticsItems.forEach(async (item) => {
      if (autoAddedDiagnosticsRef.current.has(item.vetic_variant_id)) return;
      if (cartItems.some((c) => c.vetic_variant_id === item.vetic_variant_id)) return;
      autoAddedDiagnosticsRef.current.add(item.vetic_variant_id);

      const response = await fetchLots(
        {
          clinic_id: mappedClinicId,
          transfer_inventory_type: "good_inventory",
          vetic_variant_id: item.vetic_variant_id,
          include_mapped_warehouse: true,
          exclude_low_stock: true,
        },
        "100",
        "1",
      );
      if (!response?.status || !response.data?.data) return;

      const lotsForVariant = response.data.data.filter(
        (lot) => lot.vetic_variant_id === item.vetic_variant_id && lot.quantity > 0,
      );
      if (lotsForVariant.length === 0) return;

      const highestQtyLot = lotsForVariant.reduce((max, lot) => (lot.quantity > max.quantity ? lot : max));
      // handleAddToCart clamps qty to the lot's available quantity, so this ends up min(50, quantity)
      handleAddToCart(highestQtyLot, 50, item.product_name);
    });
  }, [sectionsByCategory, mappedClinicId, requestId]);

  const handleRemoveFromCart = (idx: number) => {
    setCartItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateCartQty = (idx: number, qty: number) => {
    setCartItems((prev) => {
      const next = [...prev];
      const item = next[idx];
      if (item) next[idx] = { ...item, qty: Math.max(1, Math.min(qty, item.in_hand_qty)) };
      return next;
    });
  };

  const isSubmitting = isCreating || isUpdating;

  const mapCartItems = () =>
    cartItems.map((item) => ({
      unique_row_id: item.unique_row_id,
      qty: item.qty,
      inventory_type: item.inventory_type,
    }));

  const handleCreateDraft = async () => {
    if (!mappedClinicId || !selectedDoctorId || cartItems.length === 0) return;
    const items = mapCartItems();
    logAhsEvent(AHS_CLICK_EVENTS.SAVE_DRAFT_CLICK, {
      event_action: requestId ? "update_draft" : "create_draft",
      request_id: requestId ?? undefined,
      doctor_id: selectedDoctorId,
      items_count: items.length,
    });
    if (requestId) {
      await updateRequest(requestId, {
        clinic_id: mappedClinicId,
        target_status: "draft",
        items,
      });
    } else {
      const result = await createRequest({
        clinic_id: mappedClinicId,
        doctor_id: selectedDoctorId,
        direction: "clinic_to_doctor",
        target_status: "draft",
        items,
      });
      if (result?.status && result?.request_id) {
        setSearchParams({ request_id: result.request_id });
      }
    }
  };

  const handleCreateTransfer = async () => {
    if (!mappedClinicId || !selectedDoctorId || cartItems.length === 0) return;
    logAhsEvent(AHS_CLICK_EVENTS.CREATE_TRANSFER_REVIEW_CLICK, {
      event_action: requestId ? "update" : "create",
      request_id: requestId ?? undefined,
      doctor_id: selectedDoctorId,
      items_count: cartItems.length,
    });
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    if (!mappedClinicId || !selectedDoctorId || cartItems.length === 0) return;
    const items = mapCartItems();
    logAhsEvent(AHS_CLICK_EVENTS.CREATE_TRANSFER_CONFIRM_CLICK, {
      event_action: requestId ? "update" : "create",
      request_id: requestId ?? undefined,
      doctor_id: selectedDoctorId,
      items_count: items.length,
      transfer_items: items,
    });
    const result = requestId
      ? await updateRequest(requestId, { clinic_id: mappedClinicId, target_status: "pending", items })
      : await createRequest({ clinic_id: mappedClinicId, doctor_id: selectedDoctorId, direction: "clinic_to_doctor", target_status: "pending", items });
    if (result?.status) {
      navigate(getRouteKey("AHS_INVENTORY_TRANSFER", "url"));
    }
  };

  const selectedDoctorItem = doctorOptions.find((d) => d.id === selectedDoctorId);

  const cartColumns: TableColumn<ICartItem>[] = [
    {
      id: "index",
      header: "#",
      width: 32,
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ rowIndex }) => rowIndex + 1,
    },
    {
      id: "product_name",
      header: "Product",
      className: TABLE_CELL_MEDIUM_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => row.product_name,
    },
    {
      id: "lot_number",
      header: "Lot No.",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => row.lot_number,
    },
    {
      id: "qty",
      header: "Qty",
      overflow: "visible",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row, rowIndex }) => (
        <CartQtyEditor item={row} index={rowIndex} onQtyChange={updateCartQty} />
      ),
    },
    {
      id: "expiry",
      header: "Expiry",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.expiry_date}
          {isExpiringSoon(row.expiry_date) && (
            <span className="text-red-600 bg-[#FFEBEB] border border-red-200 px-2 py-0.5 rounded-md text-[0.65rem] font-medium whitespace-nowrap">
              Expiring Soon
            </span>
          )}
        </div>
      ),
    },
    {
      id: "mrp",
      header: "MRP",
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ row }) => `₹${row.mrp}`,
    },
    {
      id: "remove",
      header: "",
      width: 40,
      className: TABLE_CELL_CLASS,
      headerClassName: TABLE_HEADER_CLASS,
      cell: ({ rowIndex }) => (
        <button onClick={() => handleRemoveFromCart(rowIndex)} className="cursor-pointer">
          <RxCrossCircled size={18} className="text-red-400 hover:text-red-600" />
        </button>
      ),
    },
  ];

  /* Same columns as the cart, minus the remove button, with a read-only qty cell */
  const confirmColumns: TableColumn<ICartItem>[] = cartColumns
    .filter((column) => column.id !== "remove")
    .map((column) =>
      column.id === "qty" ? { ...column, overflow: undefined, cell: ({ row }) => row.qty } : column,
    );

  return (
    <>
      <div className="mx-1 my-2 sm:m-2 rounded-md bg-white px-2 sm:px-4 py-3">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-base font-semibold text-gray-900">{requestId ? "Edit Transfer Request" : "Create Transfer Request"}</h2>
          <button
            onClick={() => navigate(getRouteKey("AHS_INVENTORY_TRANSFER", "url"))}
            className="text-xs text-gray-500 hover:text-gray-700 underline cursor-pointer whitespace-nowrap"
          >
            Back to List
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 pb-3 border-b border-gray-100">
          <button
            disabled={!selectedDoctorId || cartItems.length === 0 || isSubmitting || editLoading}
            onClick={handleCreateDraft}
            className="px-5 py-2 rounded-md border border-[#D7D7D7] text-gray-700 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {isSubmitting ? "Saving..." : requestId ? "Update Draft" : "Create Draft"}
          </button>
          <button
            disabled={!selectedDoctorId || cartItems.length === 0 || isSubmitting || editLoading}
            onClick={handleCreateTransfer}
            className="px-5 py-2 rounded-md bg-[#193A6A] text-white text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#14305a]"
          >
            Create Transfer
          </button>
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium leading-5 text-[#1F1F23] mb-1.5">
            Select Doctor <span className="text-red-500">*</span>
          </label>
          <SearchableSelectMenu
            defaultSelected={defaultDoctor}
            errorMessages={[{ type: "required", message: "Doctor is required" }]}
            fieldError={formHook.formState.errors.doctor}
            register={formHook.register("doctor", { required: true })}
            onSelectItem={(item) => {
              setSelectedDoctorId(item?.id ?? null);
              if (item) formHook.clearErrors("doctor");
            }}
            selectItems={doctorOptions}
            placeholder="Search doctor by name..."
            showTooltips={false}
            showTypedErrors={false}
            showDropdownIcon={true}
            className="!h-[38px] !py-0 !text-sm !leading-5 text-gray-800"
            containerClassName="w-full sm:max-w-[470px]"
          />
          {selectedDoctorItem && (
            <p className="text-[10px] text-gray-400 mt-1 ml-1">{selectedDoctorItem.title}</p>
          )}
        </div>

        <div className="mb-5">
          <label className="block text-sm font-medium leading-5 text-[#1F1F23] mb-1.5">Add Variants</label>
          <div className="relative w-full sm:max-w-[470px]" ref={searchRef} key={variantSearchKey}>
            <div className="h-[38px] flex items-center border border-gray-300 rounded-lg bg-white overflow-hidden">
              <span className="px-2.5 text-gray-400">
                <MagnifyingGlassIcon className="w-4 h-4" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  if (value.length === 0) setSelectedCategory("ALL");
                }}
                onFocus={() => { if ((searchData?.length ?? 0) > 0) setShowResults(true); }}
                placeholder="Search product by name..."
                className="w-full h-full py-0 pr-3 text-sm text-gray-900 border-none focus:ring-0 outline-none"
              />
              {searchQuery.length >= 3 && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("ALL");
                    clearSearchResults();
                    setShowResults(false);
                  }}
                  className="pr-2 text-gray-400 hover:text-red-500 cursor-pointer"
                >
                  <RxCrossCircled size={16} />
                </button>
              )}
            </div>

            {showResults && searchQuery.length >= 3 && (
              <div className="absolute z-40 mt-1 w-full bg-white shadow-lg border border-gray-100 rounded-lg max-h-72 overflow-auto">
                <div className="sticky top-0 z-10 flex gap-2 overflow-x-auto bg-white px-3 pt-2.5 pb-2 border-b border-gray-100">
                  {CATEGORY_TABS.map((tab) => (
                    <div
                      key={tab}
                      onClick={() => setSelectedCategory(tab)}
                      className={`shrink-0 cursor-pointer text-xs rounded-2xl py-1 px-2 whitespace-nowrap border ${
                        selectedCategory === tab
                          ? "bg-black text-white border-black"
                          : "bg-[#f4f4f4] text-[#4d4f53] border-[#e2e2e2]"
                      }`}
                    >
                      {startCase(tab.toLowerCase())}
                    </div>
                  ))}
                </div>
                {searchData === null || searchData.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No products found.</p>
                ) : (
                  searchData.map((result, idx) => (
                    <div
                      key={result.vetic_variant_id ?? idx}
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedCategory("ALL");
                        setShowResults(false);
                        clearSearchResults();
                        handleVariantSelect({ name: result.vetic_variant_name, id: result.vetic_variant_id });
                      }}
                      className="px-3 py-2.5 text-sm text-gray-800 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                    >
                      {result.vetic_variant_name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mb-5">
          <p className="text-sm font-medium leading-5 text-[#1F1F23] mb-2">
            Selected Items {cartItems.length > 0 && <span className="text-gray-400">({cartItems.length})</span>}
          </p>

          {cartItems.length > 0 ? (
            <TableComponent
              columns={cartColumns}
              data={cartItems}
              getRowId={(row, index) => `${row.unique_row_id}-${index}`}
              rowClassName={(row) => (isExpiringSoon(row.expiry_date) ? "bg-[#FFEBEB]" : undefined)}
              scrollContainerClassName="mt-0 max-h-[calc(100vh-420px)] overflow-y-auto overflow-x-auto"
              minTableWidthClassName="min-w-full"
              tableClassName="rounded-[20px]"
              bodyClassName="text-xs"
            />
          ) : (
            <div className="flex justify-center items-center py-6 border rounded-lg bg-gray-50">
              <p className="text-xs font-medium text-gray-500">No items added yet. Search and add variants above.</p>
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-4">
          {MOST_SOLD_CATEGORIES.map((category) => (
            <MostSoldSuggestionList
              key={category}
              label={`Most Sold ${startCase(category)}`}
              loading={loadingByCategory[category] ?? false}
              items={sectionsByCategory[category] ?? []}
              cartItems={cartItems}
              addingVariantId={addingVariantId}
              onAdd={handleMostSoldAdd}
            />
          ))}
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-start justify-between gap-3 px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{requestId ? "Update Transfer" : "Confirm Transfer"}</p>
                <p className="text-xs text-gray-400 mt-0.5">Review the items before {requestId ? "updating" : "creating"} the transfer</p>
              </div>
              <button onClick={() => setShowConfirmModal(false)} className="shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none cursor-pointer">✕</button>
            </div>
            <div className="px-3 sm:px-5 py-4 overflow-y-auto">
              <TableComponent
                columns={confirmColumns}
                data={cartItems}
                getRowId={(row, index) => `${row.unique_row_id}-${index}`}
                rowClassName={(row) => (isExpiringSoon(row.expiry_date) ? "bg-[#FFEBEB]" : undefined)}
                scrollContainerClassName="mt-0 max-h-[45vh] overflow-y-auto overflow-x-auto"
                minTableWidthClassName="min-w-full"
                tableClassName="rounded-[20px]"
                bodyClassName="text-xs"
              />
            </div>
            <div className="px-3 sm:px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 text-xs font-medium cursor-pointer hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                onClick={handleConfirmTransfer}
                className="px-4 py-2 rounded-md bg-[#193A6A] text-white text-xs font-medium cursor-pointer disabled:opacity-50 hover:bg-[#14305a]"
              >
                {isSubmitting ? (requestId ? "Updating..." : "Creating...") : "Create Transfer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransferInventory;
